/**
 * CueScheduler — fires each cue at its server-clock time on this device.
 *
 * - Deduplicates by cue id, so reconnects never replay a reveal.
 * - Late cues still fire (the game state matters) but are flagged so the
 *   scene engine can jump to the end of an animation instead of playing it late.
 */
export class CueScheduler {
    clock;
    handler;
    seen = new Set();
    seenOrder = [];
    pending = new Map();
    lateTolerance;
    now;
    setTimer;
    clearTimer;
    constructor(clock, handler, options = {}) {
        this.clock = clock;
        this.handler = handler;
        this.lateTolerance = options.lateToleranceMs ?? 1500;
        this.now = options.now ?? (() => Date.now());
        this.setTimer = options.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
        this.clearTimer = options.clearTimer ?? ((h) => clearTimeout(h));
    }
    schedule(cue) {
        if (this.seen.has(cue.id))
            return;
        this.remember(cue.id);
        const wait = this.clock.msUntil(cue.at, this.now());
        const fire = () => {
            this.pending.delete(cue.id);
            const lateness = this.now() - this.clock.toLocal(cue.at);
            this.handler(cue, { lateness, missed: lateness > this.lateTolerance });
        };
        if (wait <= 0)
            fire();
        else
            this.pending.set(cue.id, this.setTimer(fire, wait));
    }
    get pendingCount() {
        return this.pending.size;
    }
    dispose() {
        for (const handle of this.pending.values())
            this.clearTimer(handle);
        this.pending.clear();
    }
    remember(id) {
        this.seen.add(id);
        this.seenOrder.push(id);
        if (this.seenOrder.length > 500) {
            const old = this.seenOrder.shift();
            if (old)
                this.seen.delete(old);
        }
    }
}
//# sourceMappingURL=scheduler.js.map